const MODE_KEY = "cp1_cpk_multibook_mode";
const FEATURE_ENABLED =
  String(process.env.VUE_APP_CP1_CPK_MULTIBOOK_ENABLED || "false").toLowerCase() ===
  "true";

export function isCp1MultibookEnabled() {
  return FEATURE_ENABLED;
}

export function isCp1MultibookModeActive() {
  return FEATURE_ENABLED && sessionStorage.getItem(MODE_KEY) === "Y";
}

export function isCp1Multibook(brandId) {
  return brandId === "CP1" && isCp1MultibookModeActive();
}

export function normalizeVrn(value) {
  return String(value || "")
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, "")
    .trim();
}

export async function promptForCp1Vrns(Swal, { remaining, getrans }) {
  const translate = typeof getrans === "function" ? getrans : (value) => value;
  const result = await Swal.fire({
    title: translate("Add vehicles"),
    html: `<div class="cp1-multibook-prompt">
      <div class="cp1-multibook-prompt__badge">${remaining} ${translate("booking spaces remaining")}</div>
      <p>${translate("Enter one vehicle registration per line. Each vehicle creates a separate booking and is charged the full parking price.")}</p>
    </div>`,
    input: "textarea",
    inputLabel: translate("Vehicle registrations"),
    inputPlaceholder: "AB12CDE\nXY34ZAB",
    inputAttributes: {
      "aria-label": translate("Vehicle registrations, one per line"),
      autocapitalize: "characters",
      autocomplete: "off",
      spellcheck: "false",
    },
    showCancelButton: true,
    confirmButtonText: translate("Reserve vehicles"),
    cancelButtonText: translate("Cancel"),
    confirmButtonColor: "#0878b9",
    cancelButtonColor: "#5b6470",
    reverseButtons: true,
    customClass: {
      popup: "cp1-multibook-popup",
      input: "cp1-multibook-textarea",
    },
    preConfirm: (value) => {
      const vrns = String(value || "")
        .split(/[\n,]+/)
        .map(normalizeVrn)
        .filter(Boolean);
      if (vrns.length === 0) {
        Swal.showValidationMessage(translate("Enter at least one vehicle registration."));
        return false;
      }
      if (vrns.length > remaining) {
        Swal.showValidationMessage(
          `${translate("This group exceeds the cart limit.")} ${translate("Spaces remaining")}: ${remaining}.`
        );
        return false;
      }
      if (new Set(vrns).size !== vrns.length) {
        Swal.showValidationMessage(
          translate("The same vehicle registration cannot be added twice for this date.")
        );
        return false;
      }
      return vrns;
    },
  });

  return result.isConfirmed ? result.value : null;
}

export function createMultibookRequestId() {
  if (window.crypto && typeof window.crypto.randomUUID === "function") {
    return window.crypto.randomUUID();
  }
  return `cp1-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

function escapeHtml(value) {
  return String(value || "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");
}

export async function showMultibookReserveResult(Swal, { successes, failures, getrans }) {
  const translate = typeof getrans === "function" ? getrans : (value) => value;
  const failureRows = failures
    .map(
      ({ vrn, reason }) =>
        `<li><strong>${escapeHtml(vrn)}</strong><span>${escapeHtml(reason || translate("Reservation could not be completed"))}</span></li>`
    )
    .join("");
  const partial = failures.length > 0 && successes.length > 0;
  const none = successes.length === 0;

  return Swal.fire({
    icon: none ? "error" : partial ? "warning" : "success",
    title: none
      ? translate("No vehicles were reserved")
      : partial
      ? translate("Some vehicles could not be reserved")
      : translate("Vehicles added to your basket"),
    html: `<div class="cp1-multibook-result">
      <div class="cp1-multibook-result__counts">
        <span class="is-success">${successes.length} ${translate("reserved")}</span>
        <span class="is-failed">${failures.length} ${translate("failed")}</span>
      </div>
      ${failureRows ? `<ul>${failureRows}</ul>` : ""}
    </div>`,
    confirmButtonText: translate("Continue booking"),
    confirmButtonColor: "#0878b9",
    showDenyButton: successes.length > 0,
    denyButtonText: translate("Proceed to checkout"),
    denyButtonColor: "#243b53",
    customClass: { popup: "cp1-multibook-popup" },
  });
}
